import type { ForqSupabaseClient } from './client';
import type {
  CreateIncomingInvoiceInput,
  IncomingInvoice,
  IncomingInvoiceGateway,
  ListIncomingInput,
  TransitionIncomingInput,
  UpdateAccountingInput,
} from '@forq/finance-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

/**
 * Eingangsrechnungen gegen Supabase.
 *
 * Wie der Ausgangsrechnungs-Adapter (finance.ts): die RPCs geben die Rechnung als jsonb GENAU in der
 * Form von IncomingInvoice zurueck (camelCase, Daten als YYYY-MM-DD) -- daher ein Cast statt
 * Feld-fuer-Feld-Uebersetzung; ein Test in incoming.test.ts sichert die Gestalt.
 *
 * Wichtig fuer "wasserdicht": Steuer und Bruttobetrag rechnet die DATENBANK aus Netto x Satz, nicht
 * dieser Adapter. Er schickt Netto + Satz und bekommt die fertige Rechnung zurueck.
 */
export class SupabaseIncomingInvoiceGateway implements IncomingInvoiceGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async rpc(functionName: string, args: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await this.client.rpc(functionName, args);
    if (error) throw new SupabaseOperationError(`finance.${functionName}`, messageOf(error));
    return data;
  }

  async listIncomingInvoices(input: ListIncomingInput): Promise<readonly IncomingInvoice[]> {
    const data = await this.rpc('incoming_invoice_list', {
      p_tenant_id: input.tenantId,
      p_status: input.status ?? 'all',
    });
    return (data ?? []) as readonly IncomingInvoice[];
  }

  async createIncomingInvoice(input: CreateIncomingInvoiceInput): Promise<IncomingInvoice> {
    const { tenantId, ...payload } = input;
    return this.rpc('incoming_invoice_create', { p_tenant_id: tenantId, p_payload: payload }) as Promise<IncomingInvoice>;
  }

  async updateAccounting(input: UpdateAccountingInput): Promise<IncomingInvoice> {
    return this.rpc('incoming_invoice_update_accounting', {
      p_tenant_id: input.tenantId,
      p_id: input.id,
      p_account_number: input.accountNumber,
      p_cost_center: input.costCenter,
      p_booking_text: input.bookingText,
      p_reviewer_role: input.reviewerRole,
    }) as Promise<IncomingInvoice>;
  }

  async transitionIncoming(input: TransitionIncomingInput): Promise<IncomingInvoice> {
    return this.rpc('incoming_invoice_transition', {
      p_tenant_id: input.tenantId,
      p_id: input.id,
      p_to_status: input.toStatus,
      p_note: input.note ?? '',
    }) as Promise<IncomingInvoice>;
  }
}
